import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useOutletContext, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, CheckCircle, Loader2, Package, Phone, User, Wrench, Building2, CreditCard, Lock, Eye, EyeOff, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { fbEvents } from '@/lib/facebook-pixel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicConfiguratorPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { settings } = useOutletContext() || {};
  const formContainerRef = useRef(null);
  
  const [step, setStep] = useState(1);
  const [machines, setMachines] = useState([]);
  const [options, setOptions] = useState([]);
  const [clientProfiles, setClientProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  
  // Auth state
  const [authMode, setAuthMode] = useState('none'); // 'none' = sans compte (par défaut)
  const [isClientLoggedIn, setIsClientLoggedIn] = useState(false);
  const [clientData, setClientData] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  
  const [form, setForm] = useState({
    machine_id: searchParams.get('machine') || '',
    machine_name: '',
    selected_variant_id: '',
    selected_variant_name: '',
    selected_options: [],
    client_name: '',
    client_email: '',
    client_phone: '',
    client_company: '',
    client_address: '',
    client_city: '',
    client_postal_code: '',
    client_profile: '',
    wants_financing: false,
    wants_financing_callback: false,  // Rappel pour financement
    wants_snc: false,
    wants_snc_quote: false,  // Devis avec partie SNC
    wants_demo: searchParams.get('demo') === 'true',
    accessoires_text: '',
    message: '',
  });

  // Auto-scroll to top when step changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step]);

  // Track InitiateCheckout when entering configurator
  useEffect(() => {
    fbEvents.startConfigurator();
  }, []);

  useEffect(() => {
    fetchData();
    checkClientAuth();
  }, []);

  const checkClientAuth = () => {
    const token = localStorage.getItem('client_token');
    const client = localStorage.getItem('client_data');
    if (token && client) {
      const parsedClient = JSON.parse(client);
      setClientData(parsedClient);
      setIsClientLoggedIn(true);
      setForm(f => ({
        ...f,
        client_name: parsedClient.name || f.client_name,
        client_email: parsedClient.email || f.client_email,
        client_phone: parsedClient.phone || f.client_phone
      }));
    }
  };

  const fetchData = async () => {
    try {
      const [machinesRes, optionsRes, profilesRes] = await Promise.all([
        fetch(`${API_URL}/api/public/machines`),
        fetch(`${API_URL}/api/public/options`),
        fetch(`${API_URL}/api/public/client-profiles`)
      ]);
      
      const machinesData = await machinesRes.json();
      const optionsData = await optionsRes.json();
      const profilesData = await profilesRes.json();
      
      setMachines(machinesData);
      setOptions(optionsData);
      setClientProfiles(profilesData);
      
      if (searchParams.get('machine')) {
        const selectedMachine = machinesData.find(m => m.id === searchParams.get('machine'));
        if (selectedMachine) {
          setForm(f => ({ ...f, machine_name: selectedMachine.name }));
        }
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const selectedMachine = machines.find(m => m.id === form.machine_id);
  const selectedVariant = selectedMachine?.has_variants && form.selected_variant_id
    ? selectedMachine.variants?.find(v => v.id === form.selected_variant_id)
    : null;
  
  const compatibleOptions = options.filter(opt => {
    if (!selectedMachine) return true;
    if (!opt.compatibility_by_model || Object.keys(opt.compatibility_by_model).length === 0) return true;
    const machineCompatibility = opt.compatibility_by_model[selectedMachine.id];
    return machineCompatibility && machineCompatibility.compatible === true;
  });

  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('/api/')) return `${API_URL}${url}`;
    return url;
  };

  const handleSubmit = async () => {
    if (!form.client_name || !form.client_email || !form.client_phone) {
      toast.error('Veuillez remplir tous les champs obligatoires (nom, email, téléphone)');
      return;
    }
    
    setSubmitting(true);
    try {
      const token = localStorage.getItem('client_token');
      const response = await fetch(`${API_URL}/api/public/quote-request`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          ...form,
          source: 'configurator',
          client_account_id: clientData?.id || null
        })
      });
      
      if (!response.ok) throw new Error('Submit failed');
      
      const result = await response.json();
      setSubmitResult(result);
      setSubmitted(true);
      toast.success('Votre demande a été envoyée ! Vous recevrez votre devis par email.');
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClientRegister = async () => {
    if (!form.client_name || !form.client_email || !password) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    if (password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    
    setAuthLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/public/client/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.client_name,
          email: form.client_email,
          phone: form.client_phone,
          password: password
        })
      });
      
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Erreur lors de l\'inscription');
      
      localStorage.setItem('client_token', data.access_token);
      localStorage.setItem('client_data', JSON.stringify(data.client));
      setClientData(data.client);
      setIsClientLoggedIn(true);
      toast.success('Compte créé ! Votre devis sera envoyé automatiquement.');
      
      // Auto-submit after registration
      setTimeout(() => {
        handleAutoSubmit(data.access_token, data.client);
      }, 500);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setAuthLoading(false);
    }
  };

  const handleAutoSubmit = async (token, client) => {
    if (!form.machine_id) return;
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/public/quote-request`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...form,
          client_name: client.name,
          client_email: client.email,
          client_phone: client.phone || form.client_phone,
          source: 'configurator',
          client_account_id: client.id
        })
      });
      
      if (!response.ok) throw new Error('Submit failed');
      
      const result = await response.json();
      setSubmitResult(result);
      setSubmitted(true);
    } catch (error) {
      console.error('Auto-submit error:', error);
      setStep(5); // Go to confirmation step if auto-submit fails
    } finally {
      setSubmitting(false);
    }
  };

  const handleClientLogin = async () => {
    if (!form.client_email || !loginPassword) {
      toast.error('Veuillez saisir votre email et mot de passe');
      return;
    }
    
    setAuthLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/public/client/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: form.client_email,
          password: loginPassword
        })
      });
      
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Email ou mot de passe incorrect');
      
      localStorage.setItem('client_token', data.access_token);
      localStorage.setItem('client_data', JSON.stringify(data.client));
      setClientData(data.client);
      setIsClientLoggedIn(true);
      setForm(f => ({
        ...f,
        client_name: data.client.name,
        client_email: data.client.email,
        client_phone: data.client.phone || f.client_phone
      }));
      toast.success('Connexion réussie !');
      
      // Auto-submit after login
      setTimeout(() => {
        handleAutoSubmit(data.access_token, data.client);
      }, 500);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setAuthLoading(false);
    }
  };

  const nextStep = () => {
    if (step === 1 && !form.machine_id) {
      toast.error('Veuillez sélectionner une machine');
      return;
    }
    setStep(s => Math.min(s + 1, 5));
  };

  const prevStep = () => setStep(s => Math.max(s - 1, 1));

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-[70vh] bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-sm w-full bg-white rounded-xl shadow-lg p-6 text-center">
          <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-7 h-7 text-green-500" />
          </div>
          <h2 className="text-xl font-bold text-slate-800 mb-2">Demande envoyée !</h2>
          <p className="text-sm text-slate-600 mb-1">
            Réf: <span className="font-mono font-semibold">{submitResult?.reference}</span>
          </p>
          <p className="text-sm text-slate-600 mb-6">
            Nous vous contacterons rapidement.
          </p>
          {form.wants_demo && (
            <div className="bg-yellow-50 text-yellow-800 p-3 rounded-lg mb-4 text-sm">
              🎯 Démonstration demandée
            </div>
          )}
          <div className="flex flex-col gap-2">
            <button
              onClick={() => navigate('/portail/mon-compte')}
              className="w-full py-2.5 bg-yellow-500 text-white font-medium rounded-lg hover:bg-yellow-600 transition text-sm"
            >
              Mon espace client
            </button>
            <button
              onClick={() => navigate('/portail')}
              className="w-full py-2.5 bg-slate-100 text-slate-700 font-medium rounded-lg hover:bg-slate-200 transition text-sm"
            >
              Retour à l'accueil
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" ref={formContainerRef}>
      {/* Compact Header */}
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white py-6">
        <div className="max-w-3xl mx-auto px-4">
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Barlow Condensed' }}>
            Configurez votre <span className="text-yellow-500">Machine</span>
          </h1>
          <p className="text-slate-400 text-sm mt-1">Recevez un devis personnalisé en quelques clics</p>
        </div>
      </div>

      {/* Compact Progress Steps */}
      <div className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-3xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            {[
              { num: 1, label: 'Machine', icon: Package },
              { num: 2, label: 'Accessoires', icon: Wrench },
              { num: 3, label: 'Financement', icon: CreditCard },
              { num: 4, label: 'Coordonnées', icon: User },
              { num: 5, label: 'Valider', icon: Check },
            ].map((s, index) => (
              <React.Fragment key={s.num}>
                <div 
                  className="flex flex-col items-center cursor-pointer"
                  onClick={() => s.num < step && setStep(s.num)}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all text-sm ${
                    step >= s.num ? 'bg-yellow-500 text-white' : 'bg-slate-100 text-slate-400'
                  }`}>
                    <s.icon className="w-4 h-4" />
                  </div>
                  <span className={`text-[10px] mt-1 ${step >= s.num ? 'text-yellow-600 font-medium' : 'text-slate-400'}`}>
                    {s.label}
                  </span>
                </div>
                {index < 4 && (
                  <div className={`flex-1 h-0.5 mx-1 rounded ${step > s.num ? 'bg-yellow-500' : 'bg-slate-200'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className="max-w-3xl mx-auto px-4 py-6">
        {/* Step 1: Machine Selection */}
        {step === 1 && (
          <div>
            <h2 className="text-lg font-bold text-slate-800 mb-4">Choisissez votre machine</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {machines.map(machine => (
                <button
                  key={machine.id}
                  onClick={() => {
                    setForm({ ...form, machine_id: machine.id, machine_name: machine.name });
                    // Track Facebook AddToCart
                    fbEvents.selectMachine(machine);
                    // Si la machine n'a pas de variantes, passer directement à l'étape suivante
                    if (!machine.has_variants || !machine.variants?.length) {
                      setTimeout(() => setStep(2), 300);
                    }
                  }}
                  className={`text-left bg-white rounded-lg overflow-hidden shadow-sm transition-all ${
                    form.machine_id === machine.id ? 'ring-2 ring-yellow-500 shadow-md' : 'hover:shadow-md'
                  }`}
                >
                  <div className="aspect-[4/3] bg-slate-100 relative">
                    {getImageUrl(machine.image_url) ? (
                      <img src={getImageUrl(machine.image_url)} alt={machine.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-300">
                        <Package className="w-8 h-8" />
                      </div>
                    )}
                    {form.machine_id === machine.id && (
                      <div className="absolute top-1.5 right-1.5 w-5 h-5 bg-yellow-500 rounded-full flex items-center justify-center">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </div>
                  <div className="p-2.5">
                    {machine.brand && <span className="text-[10px] font-medium text-yellow-600 uppercase">{machine.brand}</span>}
                    <h3 className="font-medium text-slate-800 text-sm leading-tight">{machine.name}</h3>
                    {machine.weight && <p className="text-xs text-slate-500">{machine.weight}</p>}
                  </div>
                </button>
              ))}
            </div>

            {/* Variant selector */}
            {selectedMachine?.has_variants && selectedMachine.variants?.length > 0 && (
              <div className="mt-6 bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                <h3 className="text-sm font-bold text-slate-800 mb-3">Version de {selectedMachine.name}</h3>
                <div className="grid grid-cols-2 gap-2">
                  {selectedMachine.variants.map(variant => (
                    <button
                      key={variant.id}
                      onClick={() => {
                        setForm({ ...form, selected_variant_id: variant.id, selected_variant_name: variant.name });
                        // Passer à l'étape suivante après sélection de la variante
                        setTimeout(() => setStep(2), 300);
                      }}
                      className={`text-left bg-white rounded-lg p-2.5 transition-all text-sm ${
                        form.selected_variant_id === variant.id ? 'ring-2 ring-yellow-500' : 'border border-slate-200 hover:shadow'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        {variant.image_url && (
                          <img src={getImageUrl(variant.image_url)} alt={variant.name} className="w-12 h-10 object-cover rounded" />
                        )}
                        <span className="font-medium text-slate-800 text-xs">{variant.name}</span>
                        {form.selected_variant_id === variant.id && (
                          <Check className="w-4 h-4 text-yellow-500 ml-auto" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Accessoires/Options */}
        {step === 2 && (
          <div>
            <h2 className="text-lg font-bold text-slate-800 mb-1">Vos accessoires</h2>
            <p className="text-slate-500 text-sm mb-4">Optionnel - indiquez les accessoires souhaités, nous les chiffrons dans votre devis</p>

            <div className="bg-white rounded-lg shadow-sm p-4">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                <Wrench className="w-4 h-4 inline-block mr-1 text-yellow-500" />
                Accessoires souhaités
              </label>
              <textarea
                value={form.accessoires_text}
                onChange={(e) => setForm({ ...form, accessoires_text: e.target.value })}
                rows={4}
                placeholder="Ex : godet curage, BRH (brise roche), attache rapide hydraulique, fourche à palettes…"
                className="w-full border border-slate-200 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
              <p className="text-xs text-slate-400 mt-2">Décrivez librement : notre équipe sélectionne les accessoires compatibles avec votre machine.</p>
            </div>
          </div>
        )}

        {/* Step 3: Activité & Financement */}
        {step === 3 && (
          <div>
            <h2 className="text-lg font-bold text-slate-800 mb-4">Votre activité & Financement</h2>
            
            <div className="bg-white rounded-lg shadow-sm p-4 space-y-4">
              {/* Activité */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-3">Quelle est votre activité ? *</label>
                <div className="flex flex-wrap gap-2">
                  {[
                    { code: 'agriculteur', name: 'Agriculteur', icon: '🌾' },
                    { code: 'btp', name: 'BTP / Terrassement / VRD', icon: '🏗️' },
                    { code: 'association', name: 'Association', icon: '🤝' },
                    { code: 'autres', name: 'Autres', icon: '📋' },
                    { code: 'particulier', name: 'Je suis un particulier', icon: '👤' }
                  ].map(activity => (
                    <button
                      key={activity.code}
                      type="button"
                      onClick={() => setForm({ ...form, client_profile: activity.code })}
                      className={`px-4 py-2.5 rounded-xl text-sm font-medium transition flex items-center gap-2 ${
                        form.client_profile === activity.code
                          ? 'bg-yellow-500 text-white shadow-lg shadow-yellow-500/30'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      <span className="text-lg">{activity.icon}</span>
                      {activity.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Message SNC si éligible (pas particulier ni autres) */}
              {form.client_profile && !['particulier', 'autres'].includes(form.client_profile) && (
                <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-4 mt-4">
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                      <span className="text-white font-bold">30%</span>
                    </div>
                    <div className="flex-1">
                      <p className="font-bold text-purple-800 mb-1">
                        🎉 Vous pouvez être éligible au dispositif SNC !
                      </p>
                      <p className="text-purple-600 text-sm mb-3">
                        Bénéficiez jusqu'à <strong>30% de réduction fiscale</strong> sur votre investissement grâce au dispositif SNC (Société en Nom Collectif).
                      </p>
                      
                      <label className="flex items-center gap-2 cursor-pointer bg-white p-3 rounded-lg border border-purple-200 hover:border-purple-400 transition">
                        <input
                          type="checkbox"
                          checked={form.wants_snc}
                          onChange={(e) => setForm({ ...form, wants_snc: e.target.checked })}
                          className="w-5 h-5 rounded border-purple-300 text-purple-500 focus:ring-purple-500"
                        />
                        <span className="text-sm text-purple-700 font-medium">
                          Oui, je souhaite bénéficier ou avoir plus d'infos sur le dispositif SNC
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {/* Section Financement */}
              <div className="space-y-3 pt-4 border-t">
                <p className="text-sm font-semibold text-slate-800">💰 Financement</p>
                
                <label className="flex items-center gap-3 cursor-pointer bg-slate-50 p-4 rounded-xl hover:bg-slate-100 transition border border-slate-200">
                  <input
                    type="checkbox"
                    checked={form.wants_financing}
                    onChange={(e) => setForm({ ...form, wants_financing: e.target.checked })}
                    className="w-5 h-5 rounded border-slate-300 text-yellow-500 focus:ring-yellow-500"
                  />
                  <div>
                    <span className="text-sm text-slate-800 font-medium flex items-center gap-2">
                      <CreditCard className="w-5 h-5 text-yellow-500" />
                      Je souhaite un financement
                    </span>
                    <p className="text-xs text-slate-500 mt-1">Crédit, leasing, LOA...</p>
                  </div>
                </label>

                {form.wants_financing && (
                  <label className="flex items-center gap-3 cursor-pointer ml-4 bg-yellow-50 border border-yellow-300 p-4 rounded-xl hover:bg-yellow-100 transition">
                    <input
                      type="checkbox"
                      checked={form.wants_financing_callback}
                      onChange={(e) => setForm({ ...form, wants_financing_callback: e.target.checked })}
                      className="w-5 h-5 rounded border-yellow-300 text-yellow-500 focus:ring-yellow-500"
                    />
                    <div>
                      <span className="text-sm text-yellow-800 font-medium flex items-center gap-2">
                        <Phone className="w-5 h-5" />
                        Souhaitez-vous être rappelé par un partenaire ?
                      </span>
                      <p className="text-xs text-yellow-600 mt-1">Pour une étude de financement personnalisée</p>
                    </div>
                  </label>
                )}
              </div>

              {/* Démonstration */}
              <div className="pt-3 border-t">
                <label className="flex items-center gap-3 cursor-pointer bg-slate-50 p-3 rounded-lg hover:bg-slate-100 transition">
                  <input
                    type="checkbox"
                    checked={form.wants_demo}
                    onChange={(e) => setForm({ ...form, wants_demo: e.target.checked })}
                    className="w-5 h-5 rounded border-slate-300 text-yellow-500 focus:ring-yellow-500"
                  />
                  <span className="text-sm text-slate-700 font-medium">🎯 Je souhaite une démonstration</span>
                </label>
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Coordonnées */}
        {step === 4 && (
          <div>
            <h2 className="text-lg font-bold text-slate-800 mb-4">Vos coordonnées</h2>
            
            <div className="bg-white rounded-lg shadow-sm p-4 space-y-4">
              {/* Logged in indicator */}
              {isClientLoggedIn && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-green-800 text-sm">Connecté(e) : {clientData?.name}</p>
                    <p className="text-xs text-green-600">Votre devis sera envoyé automatiquement</p>
                  </div>
                </div>
              )}

              {/* Contact fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Nom complet *</label>
                  <input
                    type="text"
                    value={form.client_name}
                    onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                    className="w-full px-4 py-3 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    placeholder="Jean Dupont"
                    disabled={isClientLoggedIn}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Entreprise</label>
                  <input
                    type="text"
                    value={form.client_company}
                    onChange={(e) => setForm({ ...form, client_company: e.target.value })}
                    className="w-full px-4 py-3 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    placeholder="Nom de votre société (optionnel)"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
                  <input
                    type="email"
                    value={form.client_email}
                    onChange={(e) => setForm({ ...form, client_email: e.target.value })}
                    className="w-full px-4 py-3 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    placeholder="email@exemple.com"
                    disabled={isClientLoggedIn}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Téléphone *</label>
                  <input
                    type="tel"
                    value={form.client_phone}
                    onChange={(e) => setForm({ ...form, client_phone: e.target.value })}
                    className="w-full px-4 py-3 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    placeholder="0692 XX XX XX"
                  />
                </div>
              </div>

              {/* Info - Devis par email */}
              <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                <div className="flex items-center gap-3 text-green-700">
                  <FileText className="w-6 h-6 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold">
                      Vous recevrez votre devis par email
                    </p>
                    <p className="text-xs text-green-600 mt-0.5">
                      À l'adresse : <strong>{form.client_email || 'votre email'}</strong>
                    </p>
                  </div>
                </div>
              </div>

              {/* Message optionnel */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Message (optionnel)</label>
                <textarea
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-3 text-sm border border-slate-200 rounded-xl focus:ring-2 focus:ring-yellow-500"
                  placeholder="Précisions sur votre projet, questions particulières..."
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 5: Récapitulatif & Validation */}
        {step === 5 && (
          <div>
            <h2 className="text-lg font-bold text-slate-800 mb-4">Récapitulatif de votre demande</h2>
            
            <div className="bg-white rounded-lg shadow-sm p-4 space-y-3">
              {/* Machine */}
              <div className="flex items-center gap-3 pb-3 border-b">
                {getImageUrl(selectedMachine?.image_url) && (
                  <img src={getImageUrl(selectedMachine?.image_url)} alt={form.machine_name} className="w-16 h-12 object-cover rounded" />
                )}
                <div>
                  <p className="text-xs text-slate-500">Machine sélectionnée</p>
                  <p className="font-medium text-slate-800">{form.machine_name}</p>
                  {form.selected_variant_name && <p className="text-xs text-slate-600">{form.selected_variant_name}</p>}
                </div>
                <button onClick={() => setStep(1)} className="ml-auto text-yellow-600 text-xs hover:underline">Modifier</button>
              </div>

              {/* Options */}
              {form.accessoires_text && (
                <div className="pb-3 border-b">
                  <div className="flex justify-between items-center mb-1">
                    <p className="text-xs text-slate-500">Accessoires souhaités</p>
                    <button onClick={() => setStep(2)} className="text-yellow-600 text-xs hover:underline">Modifier</button>
                  </div>
                  <p className="text-xs text-slate-700 whitespace-pre-wrap">{form.accessoires_text}</p>
                </div>
              )}

              {/* Activité & Financement */}
              <div className="pb-3 border-b">
                <div className="flex justify-between items-center mb-1">
                  <p className="text-xs text-slate-500">Activité & Financement</p>
                  <button onClick={() => setStep(3)} className="text-yellow-600 text-xs hover:underline">Modifier</button>
                </div>
                <p className="font-medium text-slate-800 text-sm capitalize">{form.client_profile?.replace('_', ' ') || 'Non précisé'}</p>
                <div className="flex flex-wrap gap-2 mt-2 text-xs">
                  {form.wants_financing && (
                    <span className="bg-green-100 text-green-700 px-2 py-1 rounded-full flex items-center gap-1">
                      <Check className="w-3 h-3" /> Financement
                    </span>
                  )}
                  {form.wants_financing_callback && (
                    <span className="bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full flex items-center gap-1">
                      <Phone className="w-3 h-3" /> Rappel partenaire
                    </span>
                  )}
                  {form.wants_snc && (
                    <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded-full flex items-center gap-1">
                      <Check className="w-3 h-3" /> SNC 30%
                    </span>
                  )}
                  {form.wants_demo && (
                    <span className="bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full">🎯 Démo</span>
                  )}
                </div>
              </div>

              {/* Contact */}
              <div className="pb-3">
                <div className="flex justify-between items-center mb-1">
                  <p className="text-xs text-slate-500">Vos coordonnées</p>
                  <button onClick={() => setStep(4)} className="text-yellow-600 text-xs hover:underline">Modifier</button>
                </div>
                <p className="font-medium text-slate-800 text-sm">{form.client_name}</p>
                {form.client_company && <p className="text-slate-600 text-sm">{form.client_company}</p>}
                <p className="text-slate-600 text-sm">{form.client_email}</p>
                <p className="text-slate-600 text-sm">{form.client_phone}</p>
              </div>

              {/* Message */}
              {form.message && (
                <div className="pt-3 border-t">
                  <p className="text-xs text-slate-500 mb-1">Message</p>
                  <p className="text-sm text-slate-700 italic">"{form.message}"</p>
                </div>
              )}
            </div>

            {/* Confirmation */}
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 mt-4">
              <div className="flex items-center gap-3 text-green-700">
                <FileText className="w-6 h-6 flex-shrink-0" />
                <div>
                  <p className="font-semibold">Votre devis sera envoyé par email</p>
                  <p className="text-sm text-green-600">À l'adresse : <strong>{form.client_email}</strong></p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-6">
          {step > 1 ? (
            <button onClick={prevStep} className="inline-flex items-center gap-1.5 px-5 py-2.5 text-slate-600 hover:text-slate-800 transition text-sm bg-slate-100 rounded-lg hover:bg-slate-200">
              <ArrowLeft className="w-4 h-4" />
              Retour
            </button>
          ) : <div />}
          
          {step < 5 ? (
            <button
              onClick={nextStep}
              disabled={(step === 1 && !form.machine_id) || (step === 3 && !form.client_profile) || (step === 4 && (!form.client_name || !form.client_email || !form.client_phone))}
              className="inline-flex items-center gap-1.5 px-6 py-2.5 bg-yellow-500 text-white font-medium rounded-lg hover:bg-yellow-600 transition disabled:opacity-50 text-sm shadow-lg shadow-yellow-500/25"
            >
              Continuer
              <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={() => {
                // Track Facebook event before submit
                fbEvents.submitQuoteRequest({ machine_name: form.machine_name });
                // Track Google Ads conversion
                if (window.gtag && settings?.google_ads_id && settings?.google_ads_conversion_label) {
                  window.gtag('event', 'conversion', {
                    send_to: `${settings.google_ads_id}/${settings.google_ads_conversion_label}`,
                    event_label: form.machine_name || 'Configurateur',
                  });
                }
                // Track GA4 event
                if (window.gtag && settings?.google_analytics_id) {
                  window.gtag('event', 'generate_lead', {
                    event_category: 'Configurateur',
                    event_label: form.machine_name,
                  });
                }
                handleSubmit();
              }}
              disabled={submitting}
              className="inline-flex items-center gap-2 px-8 py-3 bg-green-500 text-white font-semibold rounded-xl hover:bg-green-600 transition disabled:opacity-50 shadow-lg shadow-green-500/30"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Envoi en cours...
                </>
              ) : (
                <>
                  <Check className="w-5 h-5" />
                  Valider et envoyer ma demande
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
